import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { DataViewer } from '../abstract/DataViewer';
import { UserService } from './user.service';
import { SaveView } from '../clases/SaveView';
import { UpdateView } from '../clases/UpdateView';

/** Shared manual/Agent save path, always bound to the original workspace instance. */
@Injectable({ providedIn: 'root' })
export class WorkspaceViewSaveService {
  private readonly users = inject(UserService);

  async save(owner: DataViewer, options: { name?: string; saveAs?: boolean; isCurrent: () => boolean }): Promise<void> {
    owner.observeAgentCanvas();
    const revision = owner.agentCanvasState.revision;
    const lifecycle = owner.agentContextRevision;
    const existing = options.saveAs ? undefined : owner.viewFromServer;
    const name = existing?.file_name ?? options.name?.trim();
    if (!name) throw new Error('A View name is required before saving');
    const exported = await firstValueFrom(owner.onExportData());
    owner.observeAgentCanvas();
    if (!options.isCurrent() || owner.agentContextRevision !== lifecycle || owner.agentCanvasState.revision !== revision || owner.agentGestureActive) throw new Error('Workspace changed before saving');
    const connection = exported.connection;
    const identities = (key: 'hierarchy' | 'model_UUID') => {
      const values = new Set(connection.containers.map(view => view[key]).filter(Boolean));
      return values.size === 1 ? [...values][0] : undefined;
    };
    const content = JSON.stringify({ data_service_id: connection.connection.dataService.data_service_id,
      containers: connection.containers, name_value: connection.name_value });
    if (existing) {
      await firstValueFrom(this.users.modifyView(new UpdateView(name, content, existing.viewUUID, identities('hierarchy'), identities('model_UUID'))));
    } else {
      const response = await firstValueFrom(this.users.saveView(new SaveView(name, content, identities('hierarchy'), identities('model_UUID'))));
      const view = response.result?.[0];
      if (!view?.viewUUID) throw new Error('Save response did not contain a View');
      if (options.isCurrent() && owner.agentContextRevision === lifecycle) owner.setViewFromServer(view);
    }
    if (owner.agentContextRevision === lifecycle) {
      owner.agentCanvasState.savedRevision = revision;
      owner.agentCanvasState.journal.record({ type: 'VIEW_SAVED', payload: { savedRevision: revision } });
    }
  }
}
